import React from 'react'
import { useLocation } from 'react-router-dom'
import { getInboxCounters } from '../api/inbox'
import useMessagesStore from '../store/messages'
import useStationStore from '../store/station'
import useThreadsStore from '../store/threads'
import useUnreadStore from '../store/unread'
import { connectSSE } from './sse'

type StatsPayload = { unread_total?: unknown; total_unread?: unknown }

const isStatsPayload = (payload: unknown): payload is StatsPayload => {
  if (!payload || typeof payload !== 'object') {
    return false
  }
  return 'unread_total' in payload || 'total_unread' in payload
}

const RealtimeCoordinator: React.FC<{ enabled: boolean; fallbackStationId: number | null }> = ({
  enabled,
  fallbackStationId,
}) => {
  const location = useLocation()
  const selectedStationId = useStationStore((state) => state.activeStation?.id ?? null)
  const stationId = selectedStationId ?? fallbackStationId
  const setConnected = useMessagesStore((state) => state.setConnected)
  const resetMessages = useMessagesStore((state) => state.reset)
  const resetThreads = useThreadsStore((state) => state.reset)
  const resetUnread = useUnreadStore((state) => state.reset)
  const setUnreadTotal = useUnreadStore((state) => state.setTotal)
  const lightweightMode = location.pathname.startsWith('/prep')

  React.useEffect(() => {
    resetMessages()
    resetThreads()
    resetUnread()
  }, [resetMessages, resetThreads, resetUnread, stationId])

  React.useEffect(() => {
    if (!enabled || !stationId) {
      setConnected(false)
      return
    }

    if (!lightweightMode) {
      const controller = connectSSE({
        stationId,
        lastId: useMessagesStore.getState().lastId,
        lastUpdateId: useMessagesStore.getState().lastUpdateId,
        onEvent: (payload, eventType) => {
          if (eventType === 'stats' && isStatsPayload(payload)) {
            const rawValue = (payload as StatsPayload).unread_total ?? (payload as StatsPayload).total_unread ?? 0
            const value = Number(rawValue)
            if (!Number.isNaN(value)) {
              setUnreadTotal(value)
            }
          }
        },
        onOpen: () => setConnected(true),
        onError: () => setConnected(false),
      })

      return () => {
        controller.close()
        setConnected(false)
      }
    }

    setConnected(false)
    let cancelled = false
    let inFlight = false
    let timer: number | null = null

    const schedule = () => {
      if (cancelled) {
        return
      }
      if (timer !== null) {
        window.clearTimeout(timer)
      }
      const jitteredDelay = 28_000 + Math.round(Math.random() * 4_000)
      timer = window.setTimeout(() => void poll(), jitteredDelay)
    }

    const poll = async () => {
      if (cancelled || inFlight) {
        return
      }
      if (document.visibilityState === 'hidden') {
        schedule()
        return
      }
      inFlight = true
      try {
        const response = await getInboxCounters(stationId)
        if (!cancelled) {
          const value = Number(response.unread_total ?? 0)
          if (!Number.isNaN(value)) {
            setUnreadTotal(value)
          }
        }
      } catch (error) {
        if ((import.meta as any)?.env?.DEV && !cancelled) {
          console.warn('[RPA] Lightweight unread refresh failed', error)
        }
      } finally {
        inFlight = false
        schedule()
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return
      }
      if (timer !== null) {
        window.clearTimeout(timer)
        timer = null
      }
      void poll()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    void poll()

    return () => {
      cancelled = true
      if (timer !== null) {
        window.clearTimeout(timer)
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled, lightweightMode, setConnected, setUnreadTotal, stationId])

  return null
}

export default RealtimeCoordinator
